import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:3000/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Automatically attach Bearer token from localStorage to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses by clearing auth and reloading
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

/**
 * Login with email and password
 * @param {string} email
 * @param {string} password
 * @returns {Promise} response with token and user data
 */
export const login = async (email, password) => {
  const response = await api.post('/auth/login', { email, password });
  return response.data;
};

/**
 * Get portings list with optional status filter
 * @param {string} filter - Status filter (e.g., 'en_progreso' or 'finalizadas')
 * @returns {Promise} response with portings array
 */
export const getPortings = async (filter, { q, page, pageSize, from, to, sellerId } = {}) => {
  const params = {};
  if (filter) params.filter = filter;
  if (q) params.q = q;
  if (page) params.page = page;
  if (pageSize) params.pageSize = pageSize;
  if (from) params.from = from;
  if (to) params.to = to;
  if (sellerId) params.sellerId = sellerId;
  const response = await api.get('/admin/portings', { params });
  return response.data;
};

// Download portings as Excel (.xlsx), with optional date range + filter/search
export const downloadPortingsXlsx = async ({ filter, q, from, to, sellerId } = {}) => {
  const params = {};
  if (filter) params.filter = filter;
  if (q) params.q = q;
  if (from) params.from = from;
  if (to) params.to = to;
  if (sellerId) params.sellerId = sellerId;
  const response = await api.get('/admin/portings/export', { params, responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'portaciones.xlsx');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ---- Sellers ----
export const getSellers = async (q) => {
  const params = {};
  if (q) params.q = q;
  const response = await api.get('/admin/sellers', { params });
  return response.data;
};

export const createSeller = async (data) => {
  const response = await api.post('/admin/sellers', data);
  return response.data;
};

// ---- Businesses (super admin) ----
export const getBusinesses = async () => {
  const response = await api.get('/admin/businesses');
  return response.data;
};

export const createBusiness = async (data) => {
  const response = await api.post('/admin/businesses', data);
  return response.data;
};

export const updateBusiness = async (id, data) => {
  const response = await api.patch(`/admin/businesses/${id}`, data);
  return response.data;
};

// ---- Current user ----
export const getMe = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

export const updateSeller = async (id, data) => {
  const response = await api.patch(`/admin/sellers/${id}`, data);
  return response.data;
};

// ---- Commissions ----
export const getAdminCommissions = async (paid) => {
  const params = {};
  if (paid !== undefined && paid !== null && paid !== '') params.paid = paid;
  const response = await api.get('/admin/commissions', { params });
  return response.data;
};

export const payCommission = async (id, paid = true) => {
  const response = await api.patch(`/admin/commissions/${id}/pay`, { paid });
  return response.data;
};

// Download commissions CSV using the authenticated token
export const downloadCommissionsCsv = async () => {
  const response = await api.get('/admin/commissions/export', { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'comisiones.csv');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Update a porting record
 * @param {string|number} id - Porting ID
 * @param {object} data - Fields to update (status, callCenterNotes, etc.)
 * @returns {Promise} response with updated porting
 */
export const updatePorting = async (id, data) => {
  const response = await api.put(`/admin/portings/${id}`, data);
  return response.data;
};

export default api;
